import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest, ApiRequestError } from '../api/client.js';
import { Alert, Button, Field, TextInput } from '../components/ui.js';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiRequest('/api/auth/password-reset/request', {
        method: 'POST',
        body: { email: email.trim() },
      });
      setSent(true);
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The request could not be sent. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold text-slate-900">Reset your password</h1>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200">
          {sent ? (
            <div className="flex flex-col gap-4">
              {/* Deliberately does not say whether the address has an account: this page
                  is unauthenticated and would otherwise list USPS and VGS personnel. */}
              <Alert variant="success" title="Check your email">
                If that address has an account, a reset link is on its way. The link can be used
                once and expires shortly.
              </Alert>
              <Link className="text-sm text-brand-700 hover:underline" to="/sign-in">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4">
              {error && <Alert variant="error">{error}</Alert>}

              <Field label="Email address" htmlFor="email" required>
                <TextInput
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>

              <Button type="submit" loading={submitting} className="w-full">
                Email me a reset link
              </Button>

              <Link className="text-center text-sm text-brand-700 hover:underline" to="/sign-in">
                Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
